import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageModal from './LanguageModal';
import { getSavedLanguage, applyLanguage } from '../utils/language';
import './Header.css';
import shaktimanLogo from '../assets/shaktiman.png';
import kisanLogo from '../assets/kisan.png';
import bcsLogo from '../assets/bcs.png';
import agribulLogo from '../assets/agribul.png';
import tataLogo from '../assets/tata_autocomp.jpg';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState(getSavedLanguage());
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  const [activeBrand, setActiveBrand] = useState('shaktiman');
  const [productsList, setProductsList] = useState([]);
  const [otherProductsList, setOtherProductsList] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const envApiUrl = process.env.REACT_APP_API_URL?.trim();
        const API_URL = envApiUrl && (isLocalHost || (!envApiUrl.includes("localhost") && !envApiUrl.includes("127.0.0.1"))) ? envApiUrl : '/api';
        const res = await fetch(`${API_URL}/products?limit=1000`);
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          setProductsList(json.data);
        }
        try {
          const otherRes = await fetch(`${API_URL}/other-products`);
          const otherJson = await otherRes.json();
          if (otherJson.success && Array.isArray(otherJson.data)) {
            setOtherProductsList(otherJson.data);
          }
        } catch(e) {}
      } catch (err) {
        console.error('Error fetching products for suggestions:', err);
      }
    };
    fetchAllProducts();
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length >= 1) {
      const filtered = productsList.filter(prod => 
        (prod.name || '').toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 8);
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, productsList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSuggestions(false);
    }
  };

  const brandList = useMemo(() => {
    const list = [];
    const brandSet = new Set();
    otherProductsList.forEach(op => {
      if (op.brand) {
        const lowerBrand = op.brand.toLowerCase();
        if (lowerBrand !== 'spare' && lowerBrand !== 'other' && !brandSet.has(lowerBrand)) {
          brandSet.add(lowerBrand);
          list.push({
            key: lowerBrand,
            displayName: op.brand
          });
        }
      }
    });

    if (list.length === 0) {
      list.push(
        { key: 'shaktiman', displayName: 'Shaktiman' },
        { key: 'kisan', displayName: 'Kisan' },
        { key: 'bcs', displayName: 'BCS' },
        { key: 'agribull', displayName: 'Agribull' }
      );
    }
    return list;
  }, [otherProductsList]);

  useEffect(() => {
    if (brandList && brandList.length > 0 && !brandList.some(b => b.key === activeBrand) && activeBrand !== 'spare' && activeBrand !== 'other') {
      setActiveBrand(brandList[0].key);
    }
  }, [brandList, activeBrand]);

  useEffect(() => {
    applyLanguage(currentLang);
  }, [currentLang]);
  
  const hasOtherProductsNoBrand = otherProductsList.some(op => !op.brand || op.brand.trim() === '');

  return (
    <header className="main-header">
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="contact-info">
              <span onClick={() => window.location.href = 'tel:+91 96312 77264'} style={{ cursor: 'pointer' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.27-2.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                +91 96312 77264
              </span>
              <span onClick={() => navigate('/email')} style={{ cursor: 'pointer' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                ktpldsbsales@gmail.com
              </span>
              <form onSubmit={handleSearchSubmit} className="top-search-form" ref={searchContainerRef} style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder={t('search.placeholder', 'Search products...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="top-search-input"
                  onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                />
                <button type="submit" className="top-search-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                </button>
                {showSuggestions && suggestions.length > 0 && (
                  <div className="search-suggestions-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                    borderRadius: '8px',
                    marginTop: '6px',
                    zIndex: 9999,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    textAlign: 'left'
                  }}>
                    {suggestions.map(prod => {
                      const imageSrc = prod.images && prod.images[0] ? (prod.images[0].startsWith('http') ? prod.images[0] : `${process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || ''}${prod.images[0]}`) : '';
                      return (
                        <div
                          key={prod._id}
                          className="suggestion-item"
                          onClick={() => {
                            setSearchQuery('');
                            setShowSuggestions(false);
                            navigate(`/product/${prod.slug}`);
                          }}
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'background 0.2s',
                            color: '#0f172a'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {imageSrc && (
                            <img 
                              src={imageSrc} 
                              alt={prod.name} 
                              style={{ width: '32px', height: '32px', objectFit: 'contain', borderRadius: '4px', background: '#f8fafc' }} 
                            />
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>{prod.name}</span>
                            {prod.brand && <span style={{ fontSize: '10px', color: '#64748b' }}>{prod.brand}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </form>
            </div>
            <div className="language-selector" onClick={() => setIsLangModalOpen(true)}>
              <svg width="15.989" height="15.989" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <g clipPath="url(#clip0_53_705)">
                  <path d="M7.99399 14.6563C11.6733 14.6563 14.6559 11.6736 14.6559 7.99435C14.6559 4.31505 11.6733 1.33239 7.99399 1.33239C4.31469 1.33239 1.33203 4.31505 1.33203 7.99435C1.33203 11.6736 4.31469 14.6563 7.99399 14.6563Z" stroke="#4BA83D" strokeWidth="1.33239" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7.99486 1.33239C6.28423 3.12856 5.33008 5.51393 5.33008 7.99435C5.33008 10.4748 6.28423 12.8601 7.99486 14.6563C9.7055 12.8601 10.6596 10.4748 10.6596 7.99435C10.6596 5.51393 9.7055 3.12856 7.99486 1.33239Z" stroke="#4BA83D" strokeWidth="1.33239" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1.33203 7.99435H14.6559" stroke="#4BA83D" strokeWidth="1.33239" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_53_705">
                    <rect width="15.9887" height="15.9887" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              <span>{currentLang === 'en' ? 'English' : 'हिंदी'}</span>
            </div>
          </div>
        </div>
      </div>

      <nav className="nav-bar">
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <Link to="/">
                <div className="logo-img"></div>
              </Link>
            </div>

            <div className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
              )}
            </div>

            <div className={`nav-menu-wrapper ${isMobileMenuOpen ? 'open' : ''}`}>
              <ul className="nav-links">
                <li>
                  <Link to="/" className={location.pathname === '/' ? 'active' : ''} onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.home')}
                  </Link>
                </li>
                <li className="dropdown">
                  <a 
                    href="#products" 
                    className={`${location.pathname.startsWith('/products') ? 'active' : ''} mobile-dropdown-toggle`}
                    onClick={(e) => {
                      if (window.innerWidth <= 1150) {
                        e.preventDefault();
                        setIsMobileDropdownOpen(!isMobileDropdownOpen);
                      } else {
                        navigate('/products');
                        setIsMobileMenuOpen(false);
                      }
                    }}
                  >
                    {t('nav.products')} <span className="arrow-down" style={{ fontSize: '10px', marginLeft: '5px' }}>▼</span>
                  </a>
                  <div className={`dropdown-menu mega-dropdown ${isMobileDropdownOpen ? 'mobile-open' : ''}`}>
                    <div className="mega-menu-sidebar">
                      {brandList.map(brandItem => (
                        <button 
                          key={brandItem.key}
                          className={`sidebar-item ${activeBrand === brandItem.key ? 'active' : ''}`} 
                          type="button"
                          onMouseEnter={() => {
                            // Automatically fall back to first key if default activeBrand is not in brandList
                            setActiveBrand(brandItem.key);
                          }}
                          onClick={() => setActiveBrand(brandItem.key)}
                        >
                          {brandItem.displayName} Products <span className="arrow">&gt;</span>
                        </button>
                      ))}
                      <button 
                        className={`sidebar-item ${activeBrand === 'spare' ? 'active' : ''}`} 
                        type="button"
                        onMouseEnter={() => setActiveBrand('spare')}
                        onClick={() => setActiveBrand('spare')}
                      >
                        Spare Parts <span className="arrow">&gt;</span>
                      </button>
                      {hasOtherProductsNoBrand && (
                        <button 
                          className={`sidebar-item ${activeBrand === 'other' ? 'active' : ''}`} 
                          type="button"
                          onMouseEnter={() => setActiveBrand('other')}
                          onClick={() => setActiveBrand('other')}
                        >
                          Other Products <span className="arrow">&gt;</span>
                        </button>
                      )}
                    </div>

                    <div className="mega-menu-content">


                      {activeBrand && activeBrand !== 'spare' && activeBrand !== 'other' && (
                        <div className="brand-panel">
                          <h4 className="panel-title">{(brandList.find(b => b.key === activeBrand)?.displayName || '').toUpperCase()} Products</h4>
                          <div className="category-grid-links">
                            {otherProductsList.filter(op => op.brand && op.brand.toLowerCase() === activeBrand && op.status !== 'Inactive').map(op => {
                              const lowerName = op.name.toLowerCase().trim();
                              const isStandard = [
                                'rotavator', 'cultivator', 'disc harrow', 'land leveller', 'land leveler', 'mb plough', 'disc plough',
                                'power harrow', 'puddler', 'seed drill', 'planter', 'seeder/dibbler', 'seederdibbler', 'paddy machine',
                                'paddy ropak', 'fertilizer broadcaster', 'sprayer', 'power weeder (bcs)', 'power weeder/tiller',
                                'power weeder attachments', 'power weeder accessories', 'reaper', 'self propelled reaper',
                                'reaper binder', 'combine harvester', 'thresher', 'straw reaper', 'straw blower', 'straw feeder',
                                'mulcher', 'baler', 'shredder', 'hay rack', 'pump', 'pipe', 'loader', 'mud loader', 'trolley',
                                'trailer (trolley)', 'sugarcane ridger', 'potato digger', 'chaff cutter', 'chaff cutter t/d',
                                'mini mill (motor operated)', 'poultry feeder', 'age wheel', 'accessories', 'tank', 'coal breaker',
                                'special (other)', 'power equipment', 'digging equipment', 'post hole digger', 'land shaping equipment',
                                'mowers & slashers', 'mowers and slashers', 'slasher', 'arm flail mower', 'spares parts',
                                'reaper binder spare parts', 'maize seeder'
                              ].includes(lowerName);

                              const getCategorySlug = (name) => {
                                const lower = name.toLowerCase().trim();
                                if (lower === 'post hole digger') return 'digging-equipment';
                                if (lower === 'slasher' || lower === 'arm flail mower') return 'mowers-and-slashers';
                                if (lower === 'power weeder/tiller') return 'power-weeder-(bcs)';
                                if (lower === 'power weeder accessories') return 'power-weeder-attachments';
                                if (lower === 'reaper binder spare parts') return 'spares-parts';
                                if (lower === 'chaff cutter t/d') return 'chaff-cutter';
                                if (lower === 'mud loader') return 'loader';
                                if (lower === 'straw feeder') return 'straw-blower';
                                if (lower === 'trailer (trolley)') return 'trolley';
                                return lower.replace(/\s+/g, '-');
                              };

                              if (isStandard) {
                                return (
                                  <Link key={op._id} to={`/category/${getCategorySlug(op.name)}?brand=${activeBrand}`} onClick={() => setIsMobileMenuOpen(false)}>
                                    {op.name}
                                  </Link>
                                );
                              } else {
                                return (
                                  <Link key={op._id} to={`/category/other-product?otherProductCategory=${op._id}&brand=${activeBrand}`} onClick={() => setIsMobileMenuOpen(false)}>
                                    {op.name}
                                  </Link>
                                );
                              }
                            })}
                          </div>
                        </div>
                      )}

                      {activeBrand === 'spare' && (
                        <div className="brand-panel">
                          <h4 className="panel-title">Spare Parts</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '16px', padding: '15px 0' }}>
                            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px 8px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', textDecoration: 'none', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                              <img src={shaktimanLogo} alt="Shaktiman Spares" style={{ height: '36px', width: 'auto', objectFit: 'contain', maxWidth: '100%' }} />
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e293b', letterSpacing: '0.5px' }}>SHAKTIMAN</span>
                            </Link>
                            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px 8px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', textDecoration: 'none', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                              <img src={kisanLogo} alt="Kisan Spares" style={{ height: '36px', width: 'auto', objectFit: 'contain', maxWidth: '100%' }} />
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e293b', letterSpacing: '0.5px' }}>KISAN</span>
                            </Link>
                            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px 8px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', textDecoration: 'none', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                              <img src={bcsLogo} alt="BCS Spares" style={{ height: '36px', width: 'auto', objectFit: 'contain', maxWidth: '100%' }} />
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e293b', letterSpacing: '0.5px' }}>BCS</span>
                            </Link>
                            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px 8px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', textDecoration: 'none', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                              <img src={agribulLogo} alt="Agribull Spares" style={{ height: '36px', width: 'auto', objectFit: 'contain', maxWidth: '100%' }} />
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e293b', letterSpacing: '0.5px' }}>AGRIBULL</span>
                            </Link>
                            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px 8px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', textDecoration: 'none', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                              <img src={tataLogo} alt="Tata Autocomp Spares" style={{ height: '36px', width: 'auto', objectFit: 'contain', maxWidth: '100%' }} />
                              <span style={{ fontSize: '9px', fontWeight: '700', color: '#1e293b', letterSpacing: '0.2px' }}>TATA AUTOCOMP</span>
                            </Link>
                          </div>
                        </div>
                      )}

                      {activeBrand === 'other' && hasOtherProductsNoBrand && (
                        <div className="brand-panel">
                          <h4 className="panel-title">Other Products</h4>
                          <div className="category-grid-links">
                            {otherProductsList.filter(op => !op.brand || op.brand.trim() === '').map(op => (
                              <Link key={op._id} to={`/category/other-product?otherProductCategory=${op._id}&brand=other`} onClick={() => setIsMobileMenuOpen(false)}>
                                {op.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
                <li><Link to="/about" className={location.pathname === '/about' ? 'active' : ''} onClick={() => setIsMobileMenuOpen(false)}>{t('nav.about_us')}</Link></li>
                <li>
                  <Link to="/dealer-locator" className={location.pathname === '/dealer-locator' ? 'active' : ''} onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.dealer_locator')}
                  </Link>
                </li>
                <li>
                  <Link to="/service" className={location.pathname === '/service' ? 'active' : ''} onClick={() => setIsMobileMenuOpen(false)}>
                    {t('nav.service')}
                  </Link>
                </li>
                <li><Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''} onClick={() => setIsMobileMenuOpen(false)}>{t('nav.contact')}</Link></li>
              </ul>
              <div className="nav-actions">
                <button className="btn-call" onClick={() => window.location.href = 'tel:+91 96312 77264'}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.27-2.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 .7 2.81A2 2 0 0 1 22 16.92z"></path></svg>
                  {t('nav.call_us')}
                </button>
                <button className="btn-quote" onClick={() => navigate('/request-quote')}>{t('nav.request_quote')}</button>
                <button className="btn-login" onClick={() => {
                  navigate('/login');
                }}>{t('nav.login', 'Login')}</button>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <LanguageModal 
        isOpen={isLangModalOpen} 
        onClose={() => setIsLangModalOpen(false)} 
        onSelect={(lang) => {
          const langCode = lang === 'English' ? 'en' : 'hi';
          applyLanguage(langCode, { userInitiated: true });
          setCurrentLang(langCode);
        }} 
        currentLang={currentLang} 
      />
    </header>
  );
};

export default Header;


